import {
  createSlice,
  createSelector,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { uncoveryApi } from "services";

const collectionSettingsAdapter = createEntityAdapter();
const initialState = collectionSettingsAdapter.getInitialState({
  fetchAllStatus: "idle",
  fetchAllErrorMessage: "",
  deleteOneStatus: "idle",
  deleteOneErrorMessage: "",
  data: [],
  hasNextPage: false,
  page: 1,
  pageSize: 50,
  sortBy: "name",
  orderBy: "asc",
  searchTerm: "",
  toggled: false,
});

export const fetchAllCollections = createAsyncThunk(
  "collectionSettings/fetchAllCollections",
  async ({ entityId, mapId }, { rejectWithValue, getState }) => {
    const { page, pageSize, sortBy, orderBy, searchTerm } =
      getState().collectionSettings;
    const query = `/entities/${entityId}/assets/collections?cartographyId=${mapId}&page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&orderBy=${orderBy}&searchTerm=${searchTerm}`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const deleteOneCollection = createAsyncThunk(
  "collectionSettings/deleteOneCollection",
  async ({ entityId, collectionId }, { rejectWithValue }) => {
    const query = `/entities/${entityId}/assets/collections/${collectionId}`;
    try {
      const response = await uncoveryApi.delete(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const collectionSettingsSlice = createSlice({
  name: "collectionSettings",
  initialState,
  reducers: {
    updateSearchTerm: (state, { payload }) => {
      const { searchTerm } = payload;
      state.page = 1;
      state.searchTerm = searchTerm;
      return state;
    },
    sort: (state, { payload }) => {
      const { sortBy } = payload;
      state.page = 1;
      if (sortBy !== state.sortBy) {
        state.orderBy = "asc";
        state.sortBy = sortBy;
      } else {
        state.orderBy = state.orderBy === "desc" ? "asc" : "desc";
      }
      return state;
    },
    updatePage: (state, { payload }) => {
      const { page } = payload;
      state.page = page;
      return state;
    },
    setToggled: (state, { payload }) => {
      const { toggled } = payload;
      state.toggled = toggled;
    },
    removeOneCollectionSettings: (state, { payload }) => {
      const { collectionId } = payload;
      state.data = state.data.filter((c) => c.id !== collectionId);
      return state;
    },
    addOneCollectionSettings: (state, { payload }) => {
      const { collection } = payload;
      state.data = [collection].concat(state.data);
      return state;
    },
    updateOneCollectionSettings: (state, { payload }) => {
      const { collection } = payload;
      const index = state.data.findIndex((c) => c.id === collection.id);
      state.data[index] = collection;
      return state;
    },
    resetDeleteOneStatus: (state) => {
      state.deleteOneStatus = "idle";
      return state;
    },
    resetCollectionSettingsState: () => initialState,
  },
  extraReducers: {
    [fetchAllCollections.pending]: (state, { payload }) => {
      state.fetchAllStatus = "loading";
    },
    [fetchAllCollections.fulfilled]: (state, { payload }) => {
      if (state.page === 1) {
        state.data = payload.data;
      } else {
        state.data = [...state.data, ...payload.data];
      }
      state.hasNextPage = payload.pageInfo.hasNextPage;
      state.fetchAllStatus = "success";
    },
    [fetchAllCollections.rejected]: (state, { payload }) => {
      state.fetchAllErrorMessage = payload.message;
      state.fetchAllStatus = "error";
    },
    [deleteOneCollection.pending]: (state, { payload }) => {
      state.deleteOneStatus = "loading";
    },
    [deleteOneCollection.fulfilled]: (state, { payload }) => {
      state.deleteOneStatus = "success";
    },
    [deleteOneCollection.rejected]: (state, { payload }) => {
      state.deleteOneErrorMessage = payload.message;
      state.deleteOneStatus = "error";
    },
  },
});

export const {
  updateSearchTerm,
  sort,
  updatePage,
  setToggled,
  removeOneCollectionSettings,
  updateOneCollectionSettings,
  addOneCollectionSettings,
  resetDeleteOneStatus,
  resetCollectionSettingsState,
} = collectionSettingsSlice.actions;

export default collectionSettingsSlice.reducer;

const collectionSettingsState = (state) => state.collectionSettings;

export const selectCollectionIds = createSelector(
  collectionSettingsState,
  (collections) => collections.data.map((collection) => collection.id)
);
export const selectCollections = createSelector(
  collectionSettingsState,
  (collections) => collections.data
);
export const selectCollectionById = createSelector(
  [
    collectionSettingsState,
    (collectionSettingsState, collectionId) => collectionId,
  ],
  (collections, collectionId) =>
    collections.data.find((collection) => collection.id === collectionId)
);
